import { CapacitorHttp } from "@capacitor/core";
import type { HttpOptions, HttpResponse } from "@capacitor/core";

async function encodeBody(body: BodyInit | null | undefined, headers: Headers): Promise<Pick<HttpOptions, "data" | "dataType">> {
  if (body == null) return {};
  if (typeof body === "string") return { data: body };
  if (body instanceof FormData) {
    const data = [];
    for (const [key, value] of body.entries()) {
      if (typeof value === "string") data.push({ key, value, type: "string" });
      else {
        const bytes = new Uint8Array(await value.arrayBuffer());
        let binary = "";
        for (let offset = 0; offset < bytes.length; offset += 8192) binary += String.fromCharCode(...bytes.subarray(offset, offset + 8192));
        data.push({ key, value: btoa(binary), type: "base64File", contentType: value.type, fileName: value.name });
      }
    }
    headers.set("Content-Type", "multipart/form-data");
    return { data, dataType: "formData" };
  }
  throw new Error("Unsupported native API request body.");
}

export function createNativeApiFetch(
  apiPrefix: string,
  origin: string,
  request: (options: HttpOptions) => Promise<HttpResponse> = options => CapacitorHttp.request(options),
  webFetch: typeof fetch = fetch.bind(globalThis)
): typeof fetch {
  const base = new URL(apiPrefix);
  const prefix = base.pathname.replace(/\/$/, "");
  if (!["https://localhost", "capacitor://localhost", "http://localhost:51732"].includes(origin)) throw new Error("Invalid native API origin.");
  return async (input, options = {}) => {
    if (input instanceof Request) throw new Error("Native API requests require a URL and explicit options.");
    const url = new URL(String(input));
    if (url.origin !== base.origin || !url.pathname.startsWith(`${prefix}/`) || url.username || url.password || url.hash) {
      throw new Error("Native API destination is not allowed.");
    }
    const method = String(options.method || "GET").toUpperCase();
    const guestRead = method === "GET" && (
      url.pathname === `${prefix}/olivia/native-guest/session`
      || url.pathname === `${prefix}/olivia/native-guest/history`
      || new RegExp(`^${prefix}/olivia/native-guest/chat/[A-Za-z0-9_-]{8,128}/status$`).test(url.pathname)
    );
    if (!guestRead && !["POST", "PUT", "PATCH", "DELETE"].includes(method)) return webFetch(input, options);
    const signal = options.signal;
    signal?.throwIfAborted();
    const headers = new Headers(options.headers);
    headers.set("Origin", origin);
    const body = await encodeBody(options.body, headers);
    signal?.throwIfAborted();
    // Patched fetch constructs a browser Request, which strips Origin before
    // native dispatch. Use the supported HTTP API directly for writes.
    return new Promise<Response>((resolve, reject) => {
      const abort = () => reject(new DOMException("Request was cancelled.", "AbortError"));
      signal?.addEventListener("abort", abort, { once: true });
      request({ url: url.href, method, ...body, headers: Object.fromEntries(headers.entries()),
        disableRedirects: true, connectTimeout: 20000, readTimeout: 130000, responseType: "text"
      }).then(response => {
        if (signal?.aborted) return;
        const responseBody = [204, 205, 304].includes(response.status) ? null
          : typeof response.data === "string" ? response.data : JSON.stringify(response.data);
        resolve(new Response(responseBody, { status: response.status, headers: response.headers }));
      }).catch(reject).finally(() => signal?.removeEventListener("abort", abort));
    });
  };
}
